import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Eye, EyeOff, BarChart3, X, Copy, CheckCircle } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const { login, register } = useAuth();

  const handle = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = tab === "login"
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    setLoading(false);
    if (!result.success) setError(result.error);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <img src="/synq-mark.png" alt="SYNQ" className="w-10 h-10 object-contain" draggable={false} />
          <span className="font-outfit text-white text-xl font-bold tracking-tight">SYNQ</span>
        </div>
        <div>
          <h1 className="font-outfit text-5xl font-black text-white leading-none tracking-tight mb-4">
            Project<br />Intelligence<br />Under Pressure
          </h1>
          <p className="font-ibm-plex text-slate-400 text-base leading-relaxed mb-10">
            Evaluate project health across 7 strategic pillars. Detect risks early. Make better decisions.
          </p>
          <div className="grid grid-cols-3 gap-4">
            {[
              { val: "7", label: "Pillars" },
              { val: "5x", label: "Risk Accuracy" },
              { val: "100", label: "SYNQ Scale" }
            ].map((s) => (
              <div key={s.label} className="border border-slate-700 rounded-lg p-4">
                <div className="font-outfit text-2xl font-black text-white">{s.val}</div>
                <div className="font-ibm-plex text-xs text-slate-500 uppercase tracking-widest mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <p className="font-ibm-plex text-xs text-slate-600">
          &copy; {new Date().getFullYear()} Giridhar Alwar. All rights reserved.
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="/synq-mark.png" alt="SYNQ" className="w-8 h-8 object-contain" draggable={false} />
            <span className="font-outfit text-slate-900 text-lg font-bold">SYNQ — Project IQ</span>
          </div>

          <h2 className="font-outfit text-3xl font-black text-slate-900 mb-1">
            {tab === "login" ? "Welcome back" : "Create account"}
          </h2>
          <p className="font-ibm-plex text-slate-500 text-sm mb-6">
            {tab === "login" ? "Sign in to your SYNQ workspace." : "Start your project intelligence journey."}
          </p>

          {/* Google Sign-In */}
          <button
            data-testid="google-signin-btn"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors mb-4"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="font-outfit font-medium text-sm text-slate-700">Continue with Google</span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="font-ibm-plex text-xs text-slate-400 uppercase">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Tab Toggle */}
          <div className="flex border border-gray-200 rounded-lg p-1 mb-6 bg-gray-50">
            {["login", "register"].map((t) => (
              <button
                key={t}
                data-testid={`auth-tab-${t}`}
                onClick={() => { setTab(t); setError(""); }}
                className={`flex-1 py-2 text-sm font-outfit font-medium rounded-md transition-all ${
                  tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t === "login" ? "Sign In" : "Sign Up"}
              </button>
            ))}
          </div>

          <form onSubmit={handle} className="space-y-4">
            {tab === "register" && (
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Full Name</label>
                <input data-testid="auth-name-input" type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all" />
              </div>
            )}
            <div>
              <label className="block text-xs font-ibm-plex font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Email Address</label>
              <input data-testid="auth-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-ibm-plex font-semibold text-slate-700 uppercase tracking-widest">Password</label>
                {tab === "login" && (
                  <button type="button" data-testid="forgot-password-link"
                    onClick={() => { setShowForgot(true); setError(""); }}
                    className="text-xs font-ibm-plex text-blue-600 hover:text-blue-800">
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <input data-testid="auth-password-input" type={showPwd ? "text" : "password"} required minLength={6}
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 characters"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-12 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all" />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="auth-error" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">{error}</div>
            )}

            <button data-testid="auth-submit-button" type="submit" disabled={loading}
              className="w-full bg-slate-900 text-white font-outfit font-semibold py-3 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2">
              {loading ? "Please wait..." : tab === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="text-center text-sm font-ibm-plex text-slate-500 mt-6">
            {tab === "login" ? "Don't have an account? " : "Already have an account? "}
            <button data-testid="auth-toggle-link"
              onClick={() => { setTab(tab === "login" ? "register" : "login"); setError(""); }}
              className="text-blue-600 hover:text-blue-800 font-medium">
              {tab === "login" ? "Sign up free" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
      {showForgot && <ForgotPasswordModal initialEmail={form.email} onClose={() => setShowForgot(false)} />}
    </div>
  );
}

// ─── Forgot Password Modal ───────────────────────────────────────────────────
function ForgotPasswordModal({ initialEmail, onClose }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResetLink("");
    setMessage("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/forgot-password`,
        { email },
        { withCredentials: true }
      );
      setMessage(data?.message || "If an account exists, a reset link has been generated.");
      if (data?.reset_link) setResetLink(data.reset_link);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(resetLink); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
  };

  return (
    <div data-testid="forgot-password-modal" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
        <button onClick={onClose} data-testid="forgot-close-button"
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-outfit text-2xl font-black text-slate-900">Reset your password</h2>
        <p className="font-ibm-plex text-sm text-slate-500 mt-1 mb-5">
          Enter your account email. We'll generate a secure, single-use reset link.
        </p>

        {!message ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-ibm-plex font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Email Address</label>
              <input data-testid="forgot-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" />
            </div>
            {error && (
              <div data-testid="forgot-error" className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-ibm-plex">{error}</div>
            )}
            <button data-testid="forgot-submit-button" type="submit" disabled={submitting}
              className="w-full bg-slate-900 text-white font-outfit font-semibold py-2.5 rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
              {submitting ? "Generating..." : "Send Reset Link"}
            </button>
          </form>
        ) : (
          <div data-testid="forgot-success" className="space-y-3">
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="font-ibm-plex text-sm text-emerald-800">{message}</span>
            </div>
            {resetLink ? (
              <div>
                <div className="text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Your reset link (valid 60 min)</div>
                <div className="flex gap-2">
                  <input data-testid="forgot-reset-link" readOnly value={resetLink}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-xs font-ibm-plex text-slate-700 bg-slate-50" />
                  <button onClick={copy} data-testid="forgot-copy-button"
                    className="px-3 py-2 bg-slate-900 text-white rounded-lg font-outfit text-xs font-semibold hover:bg-slate-800 flex items-center gap-1.5">
                    {copied ? <><CheckCircle className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                  </button>
                </div>
                <p className="text-xs font-ibm-plex text-slate-400 mt-2">
                  Paste the link in your browser to set a new password. Email delivery is not configured yet — use this link directly.
                </p>
                <a href={resetLink.replace(/^https?:\/\/[^/]+/, "")} data-testid="forgot-open-link"
                  className="inline-block mt-3 text-sm font-ibm-plex text-blue-600 hover:text-blue-800 underline">
                  Open reset link →
                </a>
              </div>
            ) : (
              <p className="text-xs font-ibm-plex text-slate-400">
                If an account exists for this email, a reset link has been issued. Check with your administrator if you don't receive it.
              </p>
            )}
            <button onClick={onClose} data-testid="forgot-done-button"
              className="w-full mt-2 border border-gray-300 text-slate-700 font-outfit font-semibold py-2 rounded-lg hover:bg-slate-50 transition-colors">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
